import {
	type Jast,
	COMPOUND_OPERATOR_AND,
	OPERATOR_EQUALS,
	creators,
	print,
	type TerminalClause,
	type Operator,
	OPERAND_TYPE_FUNCTION,
	OPERAND_TYPE_KEYWORD,
} from '@atlaskit/jql-ast';
import type {
	SelectValue,
	PaginatedOption,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { OperandValues } from '../../common/constants.tsx';
import { escapeBackslashes } from '../../common/utils.tsx';
import { resolveOperator } from '../operators/resolve-operator.tsx';

const parseFunction = (func: string) => {
	const [, functionName, args] = func.match(/(\w+)\((.*)\)/) ?? [];
	return functionName
		? {
				functionName,
				arguments: args.split(',').map((arg) => creators.byText.argument(arg.trim())),
			}
		: { functionName: func, arguments: [] };
};
const getOperandValue = (option: PaginatedOption) => {
	switch (option.itemType) {
		case OPERAND_TYPE_FUNCTION: {
			const parsedFunctionText = parseFunction(option.value);
			return creators.functionOperand(
				creators.functionString(parsedFunctionText.functionName),
				parsedFunctionText.arguments,
			);
		}
		case OPERAND_TYPE_KEYWORD:
			return creators.keywordOperand();
		default:
			return creators.byText.valueOperand(option.value);
	}
};
const getOperandValues = (options: PaginatedOption[]) => {
	const operandValues = options.map((option) => getOperandValue(option));
	return operandValues;
};

const isListOperator = (options: PaginatedOption[]) => {
	if (options.length === 1) {
		const [option] = options;
		const keyword = Object.values(OperandValues).find(
			(kw) => kw.value === option.value || (kw.hasArgs && option.value.startsWith(kw.value)),
		);
		return Boolean(keyword?.isList);
	}

	return true;
};

const createTerminalClause = ({
	fieldId,
	options,
	terminalClause,
	operator = terminalClause?.operator || creators.operator(OPERATOR_EQUALS),
}: {
	fieldId: string;
	options: PaginatedOption[];
	terminalClause: TerminalClause | undefined;
	operator?: Operator;
}) =>
	creators.terminalClause(
		creators.field(escapeBackslashes(fieldId)),
		creators.operator(
			resolveOperator({
				operatorValue: operator.value,
				isList: isListOperator(options),
			}),
		),
		options.length > 1
			? creators.listOperand(getOperandValues(options))
			: getOperandValue(options[0]),
	);
const isNumericWithLeadingZero = (jqlTerm: string) =>
	!Number.isNaN(Number(jqlTerm)) && jqlTerm.startsWith('0');
/**
 * Helper function that wraps numeric jqlTerms in quotes which starts with a leading zero.
 * Will be moved to the backend as part of https://jplat.atlassian.net/browse/EM-9215
 */
export const quoteNumericJqlTermLeadingWithZero = (option: PaginatedOption) => {
	const jqlTerm = option.value;

	if (isNumericWithLeadingZero(jqlTerm)) {
		return { ...option, value: `"${jqlTerm}"` };
	}
	return option;
};

export const updateSelectedValue = ({
	ast,
	fieldId,
	options,
	terminalClause,
	operator,
}: {
	ast: Jast;
	fieldId: string;
	options: SelectValue;
	terminalClause: TerminalClause | undefined;
	operator?: Operator;
}): string => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const filteredOptionsWithValue = options.filter(
		(option) => 'value' in option,
	) as PaginatedOption[];

	const filteredOptions = filteredOptionsWithValue.map((option) =>
		quoteNumericJqlTermLeadingWithZero(option),
	);
	if (!terminalClause && filteredOptions.length) {
		const newClause = createTerminalClause({
			fieldId,
			options: filteredOptions,
			terminalClause,
			operator,
		});
		ast.query?.appendClause(newClause, COMPOUND_OPERATOR_AND);
	}
	if (terminalClause && !filteredOptions.length) {
		terminalClause.remove();
	} else {
		terminalClause?.replace(
			createTerminalClause({ fieldId, options: filteredOptions, terminalClause, operator }),
		);
	}
	return print(ast, {
		operatorCase: 'upper',
	});
};
